import { MUISelect, MUISwitch, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationProperties } from "../../../../../../hooks/useApplicationProperties"
import { useUsers } from "../../../../../../hooks/useUsers"
import { digitalManagerApi } from "../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../components/FormPage"
import {
  ApplicationPropertyGet,
  ApplicationPropertyPossibleValue,
} from "../../../../../models/ApplicationProperty"
import {
  ApplicationRoleApproverGet,
  ApplicationRoleApproverPost,
  ApplicationRoleApproverProperty,
  ApplicationRoleApproverTypeEnum,
} from "../../../../../models/ApplicationRoleApprover"
import { UserGet } from "../../../../../models/User"
import { ControllerRendererProps } from "../../../../../types/reactHookForm"

const validationSchema = z
  .object({
    propertyName: z.string().nullable().optional(),
    propertyValue: z.string().nullable().optional(),
    type: z.nativeEnum(ApplicationRoleApproverTypeEnum, {
      message: "Type field is required",
    }),
    isAutomatic: z.boolean(),
    sendEmail: z.boolean(),
    isGenericApprover: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.isGenericApprover) {
      if (data.propertyName === null || data.propertyName === undefined) {
        ctx.addIssue({
          code: "custom",
          path: ["propertyName"],
          message: "Property name field is required",
        })
      }
      if (data.propertyValue === null || data.propertyValue === undefined) {
        ctx.addIssue({
          code: "custom",
          path: ["propertyValue"],
          message: "Property value field is required",
        })
      }
    }
  })

export function ApplicationRoleApproverCreate() {
  const { applicationId, roleId, userId } = useParams()
  const { usersList } = useUsers(false)
  const { applicationProperties, isLoadingApplicationProperties } =
    useApplicationProperties(applicationId, true, true)
  const [userPropertiesList, setUserPropertiesList] = useState<
    ApplicationRoleApproverProperty[]
  >([])
  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}/roles/${roleId}#approvers`

  const [selectedUserId, setSelectedUserId] = useState<string>(userId ?? "")
  const [propertyPossibleValue, setPropertyPossibleValue] =
    useState<ApplicationPropertyPossibleValue>({ value: "", label: "" })

  const propertyPossibleValues: { [key: string]: ApplicationPropertyPossibleValue[] } =
    useMemo(() => {
      const propertyPossibleValuesDict: {
        [key: string]: ApplicationPropertyPossibleValue[]
      } = {}
      if (applicationProperties?.length) {
        applicationProperties.forEach((property: ApplicationPropertyGet) => {
          propertyPossibleValuesDict[property.name] = property.possibleValues
        })
      }
      return propertyPossibleValuesDict
    }, [applicationProperties])

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid, errors },
    setValue,
    control,
  } = useForm<ApplicationRoleApproverPost & { isGenericApprover: boolean }>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      propertyName: null,
      propertyValue: null,
      type: "" as ApplicationRoleApproverTypeEnum,
      isAutomatic: false,
      sendEmail: true,
      isGenericApprover: false,
    })
    setSelectedUserId(userId ?? "")
    setPropertyPossibleValue({ value: "", label: "" })
  }, [userId, reset])

  const onSubmit = (values: ApplicationRoleApproverPost) => {
    const body = {
      propertyName: isGenericApprover ? null : values.propertyName,
      propertyValue: isGenericApprover ? null : values.propertyValue,
      type: values.type,
      isAutomatic: values.isAutomatic,
      sendEmail: values.sendEmail,
    }

    return digitalManagerApi
      .post(
        `/applications/${applicationId}/roles/${roleId}/approvers/${selectedUserId}`,
        body
      )
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Approver added to role")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot add approver to role")
      })
  }

  const propertyName = useWatch({ control, name: "propertyName" })
  const propertyValue = useWatch({ control, name: "propertyValue" })
  const type = useWatch({ control, name: "type" })
  const isAutomatic = useWatch({ control, name: "isAutomatic" })
  const sendEmail = useWatch({ control, name: "sendEmail" })
  const isGenericApprover = useWatch({ control, name: "isGenericApprover" })

  const handleAutomaticToggle = useCallback(() => {
    setValue("isAutomatic", !isAutomatic, { shouldValidate: true })
  }, [isAutomatic, setValue])

  const handleSendEmailToggle = useCallback(() => {
    setValue("sendEmail", !sendEmail, { shouldValidate: true })
  }, [sendEmail, setValue])

  const handleToggleGenericApprover = useCallback(() => {
    setValue("isGenericApprover", !isGenericApprover, { shouldValidate: true })
    if (!isGenericApprover) {
      setValue("propertyName", null, { shouldValidate: true })
      setPropertyPossibleValue({ value: "", label: "" })
      setValue("propertyValue", null, { shouldValidate: true })
    }
  }, [isGenericApprover, setValue])

  useEffect(() => {
    setUserPropertiesList([])
    if (selectedUserId) {
      digitalManagerApi
        .get<ApplicationRoleApproverGet>(
          `/applications/${applicationId}/roles/${roleId}/approvers/${selectedUserId}`
        )
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            const rows = res.data.properties.map(
              (property: ApplicationRoleApproverProperty) => ({
                name: property.name,
                value: property.value,
                label: property.label,
                approverType: property.approverType,
                isAutomatic: property.isAutomatic,
                sendEmail: property.sendEmail,
              })
            )
            setUserPropertiesList(rows)
          }
        })
    }
  }, [applicationId, roleId, selectedUserId])

  useEffect(() => {
    if (
      propertyName === null ||
      propertyValue === null ||
      (type !== ApplicationRoleApproverTypeEnum.Approver &&
        type !== ApplicationRoleApproverTypeEnum.BackupApprover)
    ) {
      setValue("isAutomatic", false)
    }
  }, [propertyName, propertyValue, type, setValue])

  const handleChangeSelectPropertyName = useCallback(
    (value: string) => {
      setValue("propertyName", value, { shouldValidate: true })
      setPropertyPossibleValue({ value: "", label: "" })
      setValue("propertyValue", null, { shouldValidate: true })
    },
    [setValue]
  )

  const isSelectedUserGenericApprover =
    userPropertiesList.some((e) => e.name === null && e.value === null)

  const handleSelectChangeWithPropertyName = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationRoleApproverPost & { isGenericApprover: boolean },
        "propertyName"
      >
    ) =>
      (value: string) => {
        handleChangeSelectPropertyName(value)
        field.onChange(value)
      },
    [handleChangeSelectPropertyName]
  )

  const handlePropertyNameChange = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationRoleApproverPost & { isGenericApprover: boolean },
        "propertyName"
      >
    ) =>
      (values: unknown) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        handleSelectChangeWithPropertyName(field)(value)
      },
    [handleSelectChangeWithPropertyName]
  )

  //#region User
  const handleChangeUser = useCallback((next: Array<string | { id: string }>) => {
    const first = next?.[0]
    const selectedId = typeof first === "string" ? first : first?.id
    setSelectedUserId(selectedId ?? "")
  }, [])
  //#endregion

  //#region ApproverType

  const handleSelectChangeWithTypeUpdate = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationRoleApproverPost & { isGenericApprover: boolean },
        "type"
      >
    ) =>
      (value: ApplicationRoleApproverTypeEnum) => {
        field.onChange(value)
        setValue("type", value, { shouldValidate: true })
      },
    [setValue]
  )

  const handleRoleTypeChange = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationRoleApproverPost & { isGenericApprover: boolean },
        "type"
      >
    ) =>
      (values: unknown) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        handleSelectChangeWithTypeUpdate(field)(value as ApplicationRoleApproverTypeEnum)
      },
    [handleSelectChangeWithTypeUpdate]
  )

  const renderApplicationRoleSelect = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      ApplicationRoleApproverPost & { isGenericApprover: boolean },
      "type"
    >) => (
      <MUISelect
        name={field.name}
        id="type"
        size={32}
        dropdownLOV={Object.values(ApplicationRoleApproverTypeEnum).map((v) => ({
          id: String(v),
          name: String(v),
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleRoleTypeChange(field)}
        onBlur={field.onBlur}
        isDisabled={!selectedUserId || isSelectedUserGenericApprover}
        errors={
          errors.type?.message
            ? { type: { message: String(errors.type.message) } }
            : undefined
        }
      />
    ),
    [handleRoleTypeChange, selectedUserId, isSelectedUserGenericApprover, errors.type]
  )
  //#endregion

  //#region PropertyName
  const renderPropertyNameSelect = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      ApplicationRoleApproverPost & { isGenericApprover: boolean },
      "propertyName"
    >) => (
      <MUISelect
        name={field.name}
        id="propertyName"
        size={32}
        dropdownLOV={applicationProperties.map((property) => ({
          id: property.name,
          name: property.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handlePropertyNameChange(field)}
        onBlur={field.onBlur}
        isDisabled={
          !selectedUserId ||
          isLoadingApplicationProperties ||
          isSelectedUserGenericApprover
        }
        errors={
          errors.propertyName?.message
            ? { propertyName: { message: String(errors.propertyName.message) } }
            : undefined
        }
      />
    ),
    [
      handlePropertyNameChange,
      selectedUserId,
      isLoadingApplicationProperties,
      isSelectedUserGenericApprover,
      errors.propertyName,
      applicationProperties,
    ]
  )
  //#endregion

  //#region ProperyValue
  const handleAutocompleteGetOptionDisabled = useCallback(
    (option: ApplicationPropertyPossibleValue) => {
      const optionAlreadySelected = userPropertiesList.find(
        (applicationUserPropertiesLists: ApplicationRoleApproverProperty) =>
          propertyName === applicationUserPropertiesLists.name &&
          option.value === applicationUserPropertiesLists.value
      )
      return !!optionAlreadySelected
    },
    [userPropertiesList, propertyName]
  )

  const handleChangePropertyPossibleValue = useCallback(
    (next: Array<string | { id: string }>) => {
      const first = next?.[0]
      const selectedId = typeof first === "string" ? first : first?.id
      if (selectedId) {
        const found = (propertyPossibleValues[propertyName ?? ""] ?? []).find(
          (p) => p.value === selectedId
        )
        if (found) {
          setPropertyPossibleValue({ value: found.value, label: found.label })
          setValue("propertyValue", found.value, { shouldValidate: true })
        }
      }
    },
    [setValue, propertyPossibleValues, propertyName]
  )
  //#endregion

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={!!selectedUserId}
      isValid={isValid}
    >
      <FieldRow label="User">
        <MUISelect
          id="userId"
          name="userId"
          placeholder="Select user"
          size={32}
          dropdownLOV={usersList.map((user: UserGet) => ({
            id: user.id,
            name: user.username ?? "",
          }))}
          enableSearch
          clearable={false}
          isDisabled={!!userId}
          selectedItem={selectedUserId ? [selectedUserId] : []}
          onChange={handleChangeUser}
        />
      </FieldRow>
      <FieldRow label="Approver type">
        <>
          <Controller
            control={control}
            name="type"
            render={renderApplicationRoleSelect}
          />
          {errors.type && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                type: {
                  message:
                    typeof errors.type?.message === "string" ? errors.type.message : "",
                },
              }}
            />
          )}
        </>
      </FieldRow>
      <FieldRow label="Is Automatic">
        <MUISwitch
          size={24}
          defaultIconContent="\ead2"
          checked={isAutomatic}
          disabled={
            propertyName === null ||
            propertyValue === null ||
            (type !== ApplicationRoleApproverTypeEnum.Approver &&
              type !== ApplicationRoleApproverTypeEnum.BackupApprover)
          }
          handleChange={handleAutomaticToggle}
        />
      </FieldRow>
      <FieldRow label="Send Email">
        <MUISwitch
          size={24}
          defaultIconContent="\ead2"
          checked={sendEmail}
          handleChange={handleSendEmailToggle}
        />
      </FieldRow>
      <FieldRow label="Is generic approver">
        <MUISwitch
          size={24}
          defaultIconContent="\ead2"
          checked={isGenericApprover}
          handleChange={handleToggleGenericApprover}
          disabled={!selectedUserId || userPropertiesList.length > 0}
        />
      </FieldRow>
      {!isGenericApprover && (
        <>
          <FieldRow label="Property Name">
            <>
              <Controller
                control={control}
                name="propertyName"
                render={renderPropertyNameSelect}
              />
              {errors.propertyName && (
                <MUITextField
                  isDisabled
                  value=""
                  errors={{
                    propertyName: {
                      message:
                        typeof errors.propertyName?.message === "string"
                          ? errors.propertyName.message
                          : "",
                    },
                  }}
                />
              )}
            </>
          </FieldRow>
          <FieldRow label="Property Value">
            <MUISelect
              id="propertyPossibleValue"
              name="propertyPossibleValue"
              placeholder="Select value"
              size={32}
              dropdownLOV={(propertyPossibleValues[propertyName ?? ""] ?? []).map(
                (opt) => ({
                  id: opt.value,
                  name: opt.label ?? "",
                  isDisabled: handleAutocompleteGetOptionDisabled(opt),
                })
              )}
              enableSearch
              clearable={false}
              isDisabled={
                !selectedUserId || !propertyName || isSelectedUserGenericApprover
              }
              selectedItem={
                propertyPossibleValue.value ? [propertyPossibleValue.value] : []
              }
              onChange={handleChangePropertyPossibleValue}
              errors={
                typeof errors.propertyValue?.message === "string"
                  ? { propertyValue: { message: errors.propertyValue.message } }
                  : undefined
              }
            />
          </FieldRow>
        </>
      )}
    </FormPage>
  )
}
